import React, { useState, useEffect, useRef } from 'react';
import lodash from 'lodash';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import { BRAND_ICON_PREFIX } from '../constants';
import getThemeLookup from '../selectors/getThemeLookup';
import combineStyles from '../utils/combineStyles';
import HorizontalMenuCell from './HorizontalMenuCell';
import TouchableOpacity from './TouchableOpacity';
import { useAppSelector } from '../app/hooks';
import { DEVICE_TYPES } from '../constants';

interface ScrollHandleProps {
  position: string;
  onClick: () => void;
  backgroundColor?: string;
  iconColor?: string;
  containerBackgroundColor?: string;
}
const ScrollHandle: React.FC<ScrollHandleProps> = ({
  position = 'start',
  onClick,
  backgroundColor,
  iconColor,
  containerBackgroundColor,
}) => {
  const isStart = position === 'start';

  const IconComponent = isStart ? RiArrowLeftSLine : RiArrowRightSLine;

  return (
    <div
      style={combineStyles(
        styles.scrollHandleContainer,
        { backgroundColor: containerBackgroundColor },
        isStart ? { left: 0 } : { right: 0 },
      )}
    >
      <TouchableOpacity
        style={combineStyles(
          {
            backgroundColor,
          },
          styles.scrollHandleInnerContainer,
        )}
        onClick={onClick}
      >
        <IconComponent size={35} color={iconColor} />
      </TouchableOpacity>
    </div>
  );
};

interface HorizontalScrollableMenuProps {
  items: Brand[] | Category[];
  currentItemId: string | null;
  setCurrentItemId: (id: string) => void;
  labelThemeKey: string;
  currentLabelThemeKey: string;
  currentIndicatorThemeKey: string;
  iconSize?: number;
  containerBackgroundColor?: string;
  containerStyle?: React.CSSProperties;
  isBrand?: boolean;
  scrollContainerStyle?: React.CSSProperties;
}

const DRAG_THRESHOLD_PX = 5;

const HorizontalScrollableMenu: React.FC<HorizontalScrollableMenuProps> = props => {
  const [showScrollHandleStart, setShowScrollHandleStart] = useState<boolean>(false);
  const [showScrollHandleEnd, setScrollHandleEnd] = useState<boolean>(false);
  const deviceType = useAppSelector(state => state.deviceType);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Drag-to-scroll tracking (refs, not state - this updates on every mousemove and shouldn't
  // trigger re-renders).
  const isMouseDownRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);

  useEffect(() => {
    assess();
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    // React registers wheel listeners as passive by default, which silently blocks
    // preventDefault - attach natively so a vertical wheel/trackpad gesture can still scroll
    // this row horizontally instead of (also) scrolling the page.
    const onWheel = (e: WheelEvent) => {
      const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      if (delta !== 0) {
        e.preventDefault();
        container.scrollLeft += delta;
      }
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, []);

  const assess = () => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const processedShowScrollHandleStart =
      container.scrollLeft > 15 && deviceType != DEVICE_TYPES.MOBILE;

    const processedShowScrollHandleEnd =
      container.scrollLeft + container.offsetWidth < container.scrollWidth - 15 &&
      deviceType != DEVICE_TYPES.MOBILE;

    if (
      processedShowScrollHandleStart !== showScrollHandleStart ||
      processedShowScrollHandleEnd !== showScrollHandleEnd
    ) {
      setShowScrollHandleStart(processedShowScrollHandleStart);
      setScrollHandleEnd(processedShowScrollHandleEnd);
    }
  };

  const endDrag = () => {
    isMouseDownRef.current = false;
    isDraggingRef.current = false;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
    }
  };

  // Registered on the capture phase so it fires before HorizontalMenuCell's TouchableOpacity
  // can stopPropagation() its own mousedown - otherwise this would never see the event.
  const handleMouseDownCapture = (e: React.MouseEvent) => {
    isMouseDownRef.current = true;
    isDraggingRef.current = false;
    dragStartXRef.current = e.pageX;
    dragStartScrollLeftRef.current = scrollContainerRef.current?.scrollLeft ?? 0;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDownRef.current || !scrollContainerRef.current) {
      return;
    }

    const delta = e.pageX - dragStartXRef.current;

    if (!isDraggingRef.current && Math.abs(delta) < DRAG_THRESHOLD_PX) {
      return;
    }

    isDraggingRef.current = true;
    scrollContainerRef.current.style.cursor = 'grabbing';
    scrollContainerRef.current.scrollLeft = dragStartScrollLeftRef.current - delta;
  };

  // Also capture phase: if this mouseup follows an actual drag, stop it here so it never
  // reaches the cell underneath - TouchableOpacity fires onClick from its own mouseup handler,
  // and a drag ending on top of an item shouldn't select it.
  const handleMouseUpCapture = (e: React.MouseEvent) => {
    if (isDraggingRef.current) {
      e.stopPropagation();
    }
    endDrag();
  };

  const p = useAppSelector(getThemeLookup);
  const {
    items,
    currentItemId,
    setCurrentItemId,
    labelThemeKey,
    currentLabelThemeKey,
    currentIndicatorThemeKey,
    iconSize,
    containerStyle,
    containerBackgroundColor,
    isBrand = false,
    scrollContainerStyle,
  } = props;

  const scrollHandleProps: Omit<ScrollHandleProps, 'position' | 'onClick'> = {
    backgroundColor: p('scrollHandle', ['backgroundColor']).backgroundColor,
    iconColor: p('scrollHandle', ['color']).color,
  };

  return (
    <div
      style={combineStyles(
        { position: 'relative', overflowX: 'hidden', width: '100%' },
        containerStyle,
      )}
    >
      {showScrollHandleStart && (
        <ScrollHandle
          position="start"
          onClick={() => scrollContainerRef.current!.scrollBy({ left: -100 })}
          containerBackgroundColor={containerBackgroundColor}
          {...scrollHandleProps}
        />
      )}
      {showScrollHandleEnd && (
        <ScrollHandle
          position="end"
          onClick={() => scrollContainerRef.current!.scrollBy({ left: 100 })}
          containerBackgroundColor={containerBackgroundColor}
          {...scrollHandleProps}
        />
      )}

      <div
        ref={scrollContainerRef}
        className="hiddenScrollbars"
        style={combineStyles(styles.scrollContainer, scrollContainerStyle)}
        onScroll={lodash.throttle(() => {
          assess();
        }, 200)}
        onMouseDownCapture={handleMouseDownCapture}
        onMouseMove={handleMouseMove}
        onMouseUpCapture={handleMouseUpCapture}
        onMouseLeave={endDrag}
      >
        {lodash.map(items, item => (
          // Type Category doesnt have these fields

          <HorizontalMenuCell
            //@ts-ignore
            key={item.id}
            //@ts-ignore
            item={item}
            //@ts-ignore
            isCurrent={item.id === currentItemId}
            setCurrentItemId={setCurrentItemId}
            currentIndicatorColor={p(currentIndicatorThemeKey, ['color']).color}
            labelThemeKey={labelThemeKey}
            currentLabelThemeKey={currentLabelThemeKey}
            iconPathPrefix={BRAND_ICON_PREFIX}
            iconSize={iconSize!}
            isBrand={isBrand}
          />
        ))}
      </div>
    </div>
  );
};

const styles: Styles = {
  scrollContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    overflowX: 'auto',
    flex: 1,
    position: 'relative',
    minHeight: 65,
    padding: '0 20px',
    cursor: 'grab',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  },

  // Handles
  scrollHandleContainer: {
    height: '100%',
    position: 'absolute',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
  },
  scrollHandleInnerContainer: {
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
  },
};

export default HorizontalScrollableMenu;
